import { TableListRow, toCell } from '../components/TableList';
import { VideosVMDispatcher } from './library.viewModel';
import { Folder, Folders, Video } from '../models/library.domain';
import _ from 'lodash';
import { sprinkles } from '@design-system/sprinkles.css';
import { getDurationDisplay } from '../helpers';
import { Typography } from '@design-system/components/typography/typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faClock,
  faFolder,
} from '@fortawesome/pro-regular-svg-icons';
import { clsx } from 'clsx';
import { ColumnSchema } from '@client/new/components/StandardTable/StandardTable';
import { FilesGalleryItem } from '../components/FilesGalleryView';
import { FolderGalleryItem } from '../components/FoldersGalleryView';
import { SortFields } from '@client/new/common/library/helpers';

export type Menu = Array<{
  text: string;
  to?: string;
  href?: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  secondaryText?: string;
}>;

const getMenu = (video: Video, dispatch: VideosVMDispatcher): Menu => [
  {
    text: 'Edit',
    onClick: () => dispatch('edit', video),
  },
  {
    text: 'Download',
    href: video?.downloadUrl || '',
    onClick: () => dispatch('download'),
    disabled: !video.downloadUrl,
    secondaryText: !video.downloadUrl
      ? 'Upload in progress. File will be available soon'
      : undefined,
  },
  {
    text: 'Delete',
    onClick: () =>
      dispatch('delete', _.pick(video, ['id', 'videoSourceType', 'name'])),
    className: sprinkles({ color: 'ERROR-100' }),
  },
];

const getFolderMenu = (folder: Folder, dispatch: VideosVMDispatcher): Menu => [
  {
    text: 'Rename',
    onClick: () => dispatch('renameFolderModal', folder),
  },
  {
    text: 'Delete',
    onClick: () => dispatch('deleteFolder', folder.id),
    className: sprinkles({ color: 'ERROR-100' }),
  },
];

export const toDurationText = (duration: number): string => {
  const hh = Math.floor(duration / (60 * 60));
  const mm = Math.floor(duration / 60);
  const ss = Math.round(duration % 60);

  return [hh, mm, ss].map((v: number) => (v < 10 ? '0' + v : v + '')).join(':');
};

export const toFileRow =
  (dispatch: VideosVMDispatcher) =>
  (v: Video): TableListRow => {
    if (v.isProcessing) {
      return {
        id: v.id + '',
        cells: [
          toCell('header', {
            title: v.name.slice(0, 30) + (v.name.length >= 27 ? '...' : ''),
            showImgPlaceholder: true,
            tag: {
              icon: faClock,
              label: 'Processing...',
              className: 'bg-[#EDE5FC] text-black',
              readonly: true,
            },
          }),
          toCell('text', { text: '' }),
          toCell('text', {
            text: v?.description || '-',
            className:
              'max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap',
          }),
          toCell('text', {
            text: new Date(v.updatedAt).toLocaleDateString('en-US'),
          }),
        ],
      };
    }

    return {
      id: v.id + '',
      cells: [
        toCell('header', {
          title: v.name.slice(0, 30) + (v.name.length >= 27 ? '...' : ''),
          imageUrl: v.thumbnailUrl,
          subtitle: toDurationText(v.duration),
          onClick: () => dispatch('edit', v),
          tag: v.isProcessing
            ? {
                icon: faClock,
                label: 'Processing...',
                className: 'bg-[#EDE5FC] text-black',
              }
            : undefined,
        }),
        toCell('tag', v.type === 'short' ? 'Clip' : 'Video'),
        toCell('text', {
          text: v?.description || '-',
          className:
            'max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap',
        }),
        toCell('text', {
          text: new Date(v.updatedAt).toLocaleDateString('en-US'),
        }),
        toCell('menu', getMenu(v, dispatch)),
      ],
    };
  };

export const toFolderRow =
  (dispatch: VideosVMDispatcher) =>
  (v: Folder): TableListRow => ({
    id: v.id + '',
    cells: [
      toCell('header', {
        title: v.name.slice(0, 30) + (v.name.length >= 27 ? '...' : ''),
        icon: faFolder,
        onClick: () => dispatch('openFolder', v),
      }),
      toCell('text', {
        text: new Date(v.updatedAt).toLocaleDateString('en-US'),
      }),
      toCell('menu', getFolderMenu(v, dispatch)),
    ],
  });

export const toFilesGallery =
  (dispatch: VideosVMDispatcher, selectedIds: string[]) =>
  (v: Video): FilesGalleryItem => ({
    id: v.id + '',
    imageUrl: v.thumbnailUrl,
    name: v.name,
    tag: v.type === 'short' ? 'Clip' : 'Video',
    date: new Date(v.updatedAt).toLocaleDateString('en-US'),
    duration: getDurationDisplay(v.duration),
    onClick: (multiselect: boolean) =>
      multiselect
        ? dispatch('selectMultipleVideos', v.id)
        : dispatch('selectVideo', v.id),
    selected: selectedIds.includes(`${v.id}`),
    description: v.description,
    menu: getMenu(v, dispatch),
    isProcessing: v.isProcessing,
  });

export const toFoldersGallery =
  (dispatch: VideosVMDispatcher, selectedIds: number[]) =>
  (v: Folder): FolderGalleryItem => ({
    id: v.id,
    name: v.name,
    onClick: (multiselect: boolean) =>
      multiselect
        ? dispatch('selectMultipleFolders', v.id)
        : dispatch('selectFolder', v.id),
    onDoubleClick: () => dispatch('openFolder', v),
    selected: selectedIds.includes(v.id),
    menu: getFolderMenu(v, dispatch),
  });

export const typeToLabel = (type?: string): 'Clip' | 'Video' =>
  type === 'long' ? 'Video' : 'Clip';

export const durationToLabel = (duration: number): 'Clip' | 'Video' =>
  duration > 60 ? 'Video' : 'Clip';

export const SortGallerySelectedLabel = ({
  type,
  isAsc,
}: {
  type: SortFields;
  isAsc: boolean;
}) => {
  switch (type) {
    case 'updatedAt':
      return (
        <Typography className="flex items-center gap-2">
          <FontAwesomeIcon icon={isAsc ? faArrowUp : faArrowDown} />
          Date edited
        </Typography>
      );
    case 'name':
      return (
        <Typography className="flex items-center gap-2">
          <FontAwesomeIcon icon={isAsc ? faArrowUp : faArrowDown} />
          Name
        </Typography>
      );
  }
};

export const SortGalleryLabel = ({
  type,
  isAsc,
}: {
  type: SortFields;
  isAsc: boolean;
}) => {
  switch (type) {
    case 'updatedAt':
      return (
        <Typography className="flex items-center gap-2 justify-between">
          Date edited
          <Typography
            className={clsx(
              'flex items-center gap-2',
              sprinkles({ color: 'N-80' }),
            )}
          >
            <FontAwesomeIcon icon={isAsc ? faArrowUp : faArrowDown} />
            {isAsc ? 'Newest' : 'Oldest'}
          </Typography>
        </Typography>
      );
    case 'name':
      return (
        <Typography
          className="flex items-center gap-2 justify-between"
          variant="body"
          size="large"
        >
          Name
          <Typography
            variant="body"
            size="medium"
            className={clsx(
              'flex items-center gap-2',
              sprinkles({ color: 'N-80' }),
            )}
          >
            <FontAwesomeIcon icon={isAsc ? faArrowUp : faArrowDown} />
            {isAsc ? 'A-Z' : 'Z-A'}
          </Typography>
        </Typography>
      );
  }
};

export const sortGalleryOptions = (dispatch: VideosVMDispatcher) => [
  {
    label: SortGalleryLabel({ type: 'updatedAt', isAsc: true }),
    value: 'updatedAtDesc',
    onClick: () =>
      dispatch('sortGallery', {
        field: 'updatedAt',
        isAsc: false,
      }),
  },
  {
    label: SortGalleryLabel({ type: 'updatedAt', isAsc: false }),
    value: 'updatedAtAsc',

    onClick: () =>
      dispatch('sortGallery', {
        field: 'updatedAt',
        isAsc: true,
      }),
  },
  {
    label: SortGalleryLabel({ type: 'name', isAsc: true }),
    value: 'nameDesc',
    onClick: () =>
      dispatch('sortGallery', {
        field: 'name',
        isAsc: false,
      }),
  },
  {
    label: SortGalleryLabel({ type: 'name', isAsc: false }),
    value: 'nameAsc',
    onClick: () =>
      dispatch('sortGallery', {
        field: 'name',
        isAsc: true,
      }),
  },
];

export const getFilesSchema = ({
  field,
  isAsc,
  onClick,
}: {
  field: SortFields;
  isAsc: boolean;
  onClick: (field: SortFields) => void;
}): ColumnSchema[] => [
  {
    label: 'Name',
    width: 400,
    maxWidth: 800,
    sort: {
      onClick: () => onClick('name'),
      enabled: field === 'name',
      isAsc,
    },
  },
  { label: 'Content Type', width: 90, justify: 'center', maxWidth: '20%' },
  { label: 'Description', width: 100, justify: 'center', maxWidth: 200 },
  {
    label: 'Date edited',
    width: 150,
    justify: 'center',
    maxWidth: '20%',
    sort: {
      onClick: () => onClick('updatedAt'),
      enabled: field === 'updatedAt',
      isAsc,
    },
  },
  { label: 'Actions', width: 130, justify: 'center', maxWidth: 130 },
];

export const getFoldersSchema = ({
  field,
  isAsc,
  sort,
  onClick,
}: {
  field: SortFields;
  isAsc: boolean;
  sort: (field: SortFields) => void;
  onClick: (value: Folder) => void;
}): ColumnSchema[] => [
  {
    label: 'Name',
    width: 400,
    maxWidth: 1000,
    onClick: (value: Folder) => onClick(value),
    sort: {
      onClick: () => sort('name'),
      enabled: field === 'name',
      isAsc,
    },
  },
  {
    label: 'Date edited',
    width: 250,
    justify: 'center',
    maxWidth: '20%',
    sort: {
      onClick: () => sort('updatedAt'),
      enabled: field === 'updatedAt',
      isAsc,
    },
  },
  { label: 'Actions', width: 130, justify: 'center', maxWidth: 130 },
];

export const getFoldersBreadcrumbs = ({
  allFolders,
  openedFolderId,
  dispatch,
}: {
  allFolders: Folders;
  openedFolderId: number | null;
  dispatch: VideosVMDispatcher;
}) => {
  if (!openedFolderId) return [];

  const breadcrumbFolders = [];
  let currentId: number | undefined = openedFolderId;
  do {
    const folder = allFolders.find((f) => f.id === currentId);
    folder && breadcrumbFolders.unshift(folder);
    currentId = folder?.parentId || undefined;
  } while (currentId);

  return breadcrumbFolders.map((f) => ({
    label: f.name,
    onClick: () => dispatch('breadcrumbClicked', f),
    id: f.id,
  }));
};
